(() => {
  class FindYourShape {
    constructor(section) {
      this.section = section;

      this.viewport = section.querySelector(
        '[data-find-shape-viewport]'
      );

      this.track = section.querySelector(
        '[data-find-shape-track]'
      );

      this.slides = [
        ...section.querySelectorAll(
          '[data-find-shape-slide]'
        )
      ];

      this.nextButton = section.querySelector(
        '[data-find-shape-next]'
      );

      this.prevButton = section.querySelector(
        '[data-find-shape-prev]'
      );

      this.progressTrack = section.querySelector(
        '[data-find-shape-progress-track]'
      );

      this.progress = section.querySelector(
        '[data-find-shape-progress]'
      );

      if (
        !this.viewport ||
        !this.track ||
        !this.slides.length
      ) {
        return;
      }

      this.currentIndex = 0;

      this.startX = 0;
      this.currentX = 0;
      this.startTranslate = 0;

      this.isDragging = false;
      this.hasMoved = false;

      /*
        TRUE only after actual drag/swipe.
        Used to prevent accidental link click
        after swipe.
      */
      this.preventNextClick = false;

      this.slideWidth = 0;
      this.maxTranslate = 0;

      this.resizeObserver = null;

      this.init();
    }


    /* =====================================================
       INIT
    ===================================================== */

    init() {
      this.updateDimensions();

      this.bindEvents();

      this.updateSlider(false);

      this.observeResize();
    }


    /* =====================================================
       GET CARDS VISIBLE
    ===================================================== */

    getCardsVisible() {
      const isMobile = window.matchMedia(
        '(max-width: 749px)'
      ).matches;

      const variable = isMobile
        ? '--fys-mobile-cards'
        : '--fys-desktop-cards';

      const value = parseFloat(
        getComputedStyle(this.section)
          .getPropertyValue(variable)
      );

      return value || (isMobile ? 1.1 : 5);
    }


    /* =====================================================
       UPDATE DIMENSIONS
    ===================================================== */

    updateDimensions() {
      const viewportWidth =
        this.viewport.clientWidth;

      if (!viewportWidth) {
        return;
      }

      const styles =
        window.getComputedStyle(this.track);

      const gap =
        parseFloat(
          styles.columnGap ||
          styles.gap ||
          0
        ) || 0;

      const cardsVisible =
        this.getCardsVisible();

      const cardWidth =
        (
          viewportWidth -
          gap * (cardsVisible - 1)
        ) / cardsVisible;

      this.slideWidth =
        cardWidth + gap;

      this.section.style.setProperty(
        '--fys-slide-width',
        `${cardWidth}px`
      );

      /*
        Use scrollWidth after card width
        has been applied.
      */

      this.maxTranslate =
        Math.max(
          0,
          this.track.scrollWidth -
          viewportWidth
        );
    }


    /* =====================================================
       MAX INDEX
    ===================================================== */

    getMaxIndex() {
      if (
        this.slideWidth <= 0 ||
        this.maxTranslate <= 0
      ) {
        return 0;
      }

      return Math.ceil(
        this.maxTranslate /
        this.slideWidth
      );
    }


    /* =====================================================
       CURRENT TRANSLATE
    ===================================================== */

    getCurrentTranslate() {
      const transform =
        window.getComputedStyle(
          this.track
        ).transform;

      if (
        !transform ||
        transform === 'none'
      ) {
        return 0;
      }

      const match =
        transform.match(
          /matrix.*\((.+)\)/
        );

      if (!match) {
        return 0;
      }

      const values =
        match[1].split(',');

      return (
        parseFloat(values[4]) || 0
      );
    }


    /* =====================================================
       SET TRANSLATE
    ===================================================== */

    setTranslate(
      value,
      animate = true
    ) {
      const translate =
        Math.max(
          -this.maxTranslate,
          Math.min(
            0,
            value
          )
        );

      if (!animate) {
        this.track.style.transition =
          'none';
      } else {
        this.track.style.transition = '';
      }

      this.track.style.transform =
        `translate3d(${translate}px, 0, 0)`;

      if (!animate) {
        requestAnimationFrame(() => {
          this.track.style.transition = '';
        });
      }

      this.updateProgress(
        Math.abs(translate)
      );
    }


    /* =====================================================
       UPDATE SLIDER
    ===================================================== */

    updateSlider(
      animate = true
    ) {
      this.updateDimensions();

      const maxIndex =
        this.getMaxIndex();

      this.currentIndex =
        Math.min(
          this.currentIndex,
          maxIndex
        );

      let translate = 0;

      /*
        Always align the final position
        exactly to the end.
      */

      if (
        this.currentIndex >= maxIndex &&
        maxIndex > 0
      ) {
        translate =
          -this.maxTranslate;
      } else {
        translate =
          -(
            this.currentIndex *
            this.slideWidth
          );
      }

      this.setTranslate(
        translate,
        animate
      );

      this.updateActiveCard();

      this.updateArrow();

      this.updateProgress(
        Math.abs(translate)
      );
    }


    /* =====================================================
       NEXT
    ===================================================== */

    next() {
      const maxIndex =
        this.getMaxIndex();

      if (
        this.currentIndex >= maxIndex
      ) {
        return;
      }

      this.currentIndex++;

      this.updateSlider(true);
    }


    /* =====================================================
       PREVIOUS
    ===================================================== */

    previous() {
      if (
        this.currentIndex <= 0
      ) {
        return;
      }

      this.currentIndex--;

      this.updateSlider(true);
    }


    /* =====================================================
       ACTIVE CARD
    ===================================================== */

    updateActiveCard() {
      this.slides.forEach(
        (slide, index) => {

          const card =
            slide.querySelector(
              '[data-shape-card]'
            );

          if (!card) {
            return;
          }

          card.classList.toggle(
            'is-active',
            index === this.currentIndex
          );
        }
      );
    }


    /* =====================================================
       PROGRESS
    ===================================================== */

    updateProgress(
      translate = 0
    ) {
      if (
        !this.progress ||
        !this.progressTrack
      ) {
        return;
      }

      const trackWidth =
        this.progressTrack.clientWidth;

      const barWidth =
        this.progress.offsetWidth;

      const availableSpace =
        Math.max(
          0,
          trackWidth - barWidth
        );

      let percentage = 0;

      if (
        this.maxTranslate > 0
      ) {
        percentage =
          translate /
          this.maxTranslate;
      }

      percentage =
        Math.max(
          0,
          Math.min(
            1,
            percentage
          )
        );

      const position =
        availableSpace *
        percentage;

      this.progress.style.transform =
        `translate3d(${position}px, 0, 0)`;
    }


    /* =====================================================
       ARROWS
    ===================================================== */

    updateArrow() {
      const maxIndex =
        this.getMaxIndex();

      /*
        Previous
      */

      if (this.prevButton) {
        this.prevButton.classList.toggle(
          'is-hidden',
          this.currentIndex <= 0
        );
      }

      /*
        Next
      */

      if (this.nextButton) {
        this.nextButton.classList.toggle(
          'is-hidden',
          maxIndex <= 0 ||
          this.currentIndex >= maxIndex
        );
      }
    }


    /* =====================================================
       POINTER DOWN
    ===================================================== */

    pointerDown(event) {

      /*
        Ignore right mouse button.
      */

      if (
        event.pointerType === 'mouse' &&
        event.button !== 0
      ) {
        return;
      }

      /*
        Don't start dragging when
        clicking navigation arrows.
      */

      if (
        event.target.closest(
          '[data-find-shape-prev], [data-find-shape-next]'
        )
      ) {
        return;
      }

      this.isDragging = true;

      this.hasMoved = false;

      this.preventNextClick = false;

      this.startX =
        event.clientX;

      this.currentX =
        event.clientX;

      this.startTranslate =
        this.getCurrentTranslate();

      this.viewport.classList.add(
        'is-dragging'
      );

      /*
        IMPORTANT:

        We intentionally DO NOT call
        preventDefault() here.

        This keeps normal <a> click
        behavior working.
      */

      /*
        IMPORTANT:

        Do NOT use pointer capture.

        Pointer capture can interfere
        with native anchor click behavior
        in some Shopify/browser combinations.
      */
    }


    /* =====================================================
       POINTER MOVE
    ===================================================== */

    pointerMove(event) {

      if (!this.isDragging) {
        return;
      }

      this.currentX =
        event.clientX;

      const diff =
        this.currentX -
        this.startX;

      /*
        Only start drag after 8px movement.
      */

      if (
        Math.abs(diff) > 8
      ) {
        this.hasMoved = true;
      }

      /*
        Normal click.

        Do nothing.
      */

      if (!this.hasMoved) {
        return;
      }

      /*
        Actual drag.
      */

      event.preventDefault();

      this.setTranslate(
        this.startTranslate +
        diff,
        false
      );
    }


    /* =====================================================
       POINTER UP
    ===================================================== */

    pointerUp(event) {

      if (!this.isDragging) {
        return;
      }

      this.isDragging = false;

      this.viewport.classList.remove(
        'is-dragging'
      );

      const diff =
        this.currentX -
        this.startX;

      const absDiff =
        Math.abs(diff);

      const threshold =
        Math.min(
          80,
          this.viewport.clientWidth *
          0.15
        );


      /*
        =================================================
        NORMAL CLICK
        =================================================

        User simply clicked the card.

        IMPORTANT:
        Do NOT prevent the click.

        Native <a href="..."> navigation
        will work normally.
      */

      if (
        absDiff <= 8
      ) {
        this.hasMoved = false;
        this.preventNextClick = false;

        return;
      }


      /*
        =================================================
        ACTUAL DRAG / SWIPE
        =================================================
      */

      this.hasMoved = true;

      if (
        absDiff > threshold
      ) {

        if (diff < 0) {
          this.next();
        } else {
          this.previous();
        }

      } else {

        /*
          Small movement:
          return to nearest slide.
        */

        this.updateSlider(true);
      }


      /*
        Prevent the synthetic click
        generated after dragging.
      */

      this.preventNextClick = true;


      /*
        Reset after browser click event.
      */

      setTimeout(() => {
        this.preventNextClick = false;
        this.hasMoved = false;
      }, 50);
    }


    /* =====================================================
       CANCEL
    ===================================================== */

    pointerCancel() {

      if (!this.isDragging) {
        return;
      }

      this.isDragging = false;

      this.hasMoved = false;

      this.preventNextClick = false;

      this.viewport.classList.remove(
        'is-dragging'
      );

      this.updateSlider(true);
    }


    /* =====================================================
       EVENTS
    ===================================================== */

    bindEvents() {

      /*
        ===============================================
        PREVIOUS
        ===============================================
      */

      if (this.prevButton) {

        this.prevButton.addEventListener(
          'click',
          (event) => {

            event.preventDefault();
            event.stopPropagation();

            this.previous();

          }
        );
      }


      /*
        ===============================================
        NEXT
        ===============================================
      */

      if (this.nextButton) {

        this.nextButton.addEventListener(
          'click',
          (event) => {

            event.preventDefault();
            event.stopPropagation();

            this.next();

          }
        );
      }


      /*
        ===============================================
        POINTER DOWN
        ===============================================
      */

      this.viewport.addEventListener(
        'pointerdown',
        (event) => {
          this.pointerDown(event);
        }
      );


      /*
        ===============================================
        POINTER MOVE
        ===============================================
      */

      this.viewport.addEventListener(
        'pointermove',
        (event) => {
          this.pointerMove(event);
        },
        {
          passive: false
        }
      );


      /*
        ===============================================
        POINTER UP
        ===============================================
      */

      this.viewport.addEventListener(
        'pointerup',
        (event) => {
          this.pointerUp(event);
        }
      );


      /*
        ===============================================
        POINTER CANCEL
        ===============================================
      */

      this.viewport.addEventListener(
        'pointercancel',
        () => {
          this.pointerCancel();
        }
      );


      /*
        ===============================================
        CARD / A TAG CLICK
        ===============================================

        IMPORTANT:

        Normal click:
        allow the browser to open href.

        Drag:
        block accidental click.
      */

      this.viewport.addEventListener(
        'click',
        (event) => {

          const link =
            event.target.closest(
              'a[data-shape-card]'
            );

          /*
            Not a card link.
            Nothing to do.
          */

          if (!link) {
            return;
          }


          /*
            If user just dragged,
            stop the accidental link click.
          */

          if (
            this.preventNextClick
          ) {

            event.preventDefault();
            event.stopPropagation();

            this.preventNextClick = false;

            return;
          }


          /*
            NORMAL CLICK

            IMPORTANT:

            Do NOT call:

            preventDefault()
            stopPropagation()

            Browser will naturally open
            the href from the <a>.
          */

        },
        false
      );


      /*
        ===============================================
        KEYBOARD
        ===============================================
      */

      this.section.addEventListener(
        'keydown',
        (event) => {

          const target =
            event.target;

          /*
            Don't interfere with form fields.
          */

          if (
            target &&
            (
              target.matches('input') ||
              target.matches('textarea') ||
              target.matches('select') ||
              target.isContentEditable
            )
          ) {
            return;
          }


          /*
            Right arrow
          */

          if (
            event.key === 'ArrowRight'
          ) {

            event.preventDefault();

            this.next();
          }


          /*
            Left arrow
          */

          if (
            event.key === 'ArrowLeft'
          ) {

            event.preventDefault();

            this.previous();
          }

        }
      );


      /*
        ===============================================
        PREVENT IMAGE DRAG
        ===============================================
      */

      this.section
        .querySelectorAll('img')
        .forEach((image) => {

          image.addEventListener(
            'dragstart',
            (event) => {
              event.preventDefault();
            }
          );

        });


      /*
        ===============================================
        RESIZE
        ===============================================
      */

      window.addEventListener(
        'resize',
        () => {

          this.updateSlider(false);

        },
        {
          passive: true
        }
      );
    }


    /* =====================================================
       RESIZE OBSERVER
    ===================================================== */

    observeResize() {

      if (
        typeof ResizeObserver ===
        'undefined'
      ) {
        return;
      }

      this.resizeObserver =
        new ResizeObserver(() => {

          this.updateSlider(false);

        });

      this.resizeObserver.observe(
        this.viewport
      );
    }
  }


  /* =======================================================
     INITIALIZE
  ======================================================= */

  function initFindYourShape(
    root = document
  ) {

    const sections =
      root.querySelectorAll(
        '[data-find-shape]'
      );

    sections.forEach(
      (section) => {

        if (
          section.__findYourShapeInstance
        ) {
          return;
        }

        section.__findYourShapeInstance =
          new FindYourShape(
            section
          );

      }
    );
  }


  /* =======================================================
     DOM READY
  ======================================================= */

  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initFindYourShape();
      }
    );

  } else {

    initFindYourShape();

  }


  /* =======================================================
     SHOPIFY THEME EDITOR
  ======================================================= */

  document.addEventListener(
    'shopify:section:load',
    (event) => {

      initFindYourShape(
        event.target
      );

    }
  );


  /* =======================================================
     SHOPIFY SECTION UNLOAD
  ======================================================= */

  document.addEventListener(
    'shopify:section:unload',
    (event) => {

      const section =
        event.target.querySelector?.(
          '[data-find-shape]'
        );

      if (
        section &&
        section.__findYourShapeInstance
      ) {

        const instance =
          section.__findYourShapeInstance;

        if (
          instance.resizeObserver
        ) {

          instance.resizeObserver.disconnect();

        }

      }

    }
  );

})();